import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { localStorageTokenStorage, PnlApiError } from '@pnl/sdk';
import { api } from '../lib/api';
import { fetchRoles, useAuthStore } from '../auth/auth.store';
import { AuthLayout } from '../components/AuthLayout';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setSession);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from =
    (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/dashboard';

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const session = await api.login({ email, password });
      localStorageTokenStorage('pnl_trader_').setTokens(session.accessToken, session.refreshToken);
      // roles (best-effort) junto do perfil: revela menus por role (ex.: Afiliados)
      const [me, roles] = await Promise.all([api.getTraderMe(), fetchRoles()]);
      setSession(
        {
          id: me.user.id,
          email: me.user.email,
          fullName: me.user.fullName,
          marketCode: me.trader?.marketCode ?? null,
        },
        roles,
      );
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof PnlApiError) {
        if (err.api.code === 'INVALID_CREDENTIALS') setError('Email ou senha inválidos.');
        else if (err.api.code === 'EMAIL_NOT_VERIFIED')
          setError('Verifique seu email antes de logar.');
        else if (err.api.code === 'USER_BLOCKED') setError('Conta bloqueada. Contate o suporte.');
        else if (err.api.code === 'RATE_LIMITED')
          setError('Muitas tentativas. Aguarde alguns minutos.');
        else setError(err.api.message);
      } else {
        setError('Erro de conexão. Tente novamente.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout>
      <h1 className="font-display text-ink-100 text-xl font-semibold">Acesse sua conta</h1>
      <p className="text-ink-400 mt-1 text-sm">Use o email cadastrado na compra do seu plano.</p>

      {error && (
        <div className="bg-danger-500/10 border-danger-500/30 text-danger-600 mt-5 rounded-lg border px-3 py-2 text-sm">
          {error}
        </div>
      )}

      <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="email" className="text-ink-300 mb-1.5 block text-xs font-semibold">
            Email
          </label>
          <div className="relative">
            <Mail size={14} className="text-ink-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
              className="border-ink-800 text-ink-100 placeholder:text-ink-500 focus:border-navy-primary focus:ring-navy-primary/20 w-full rounded-lg border bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:ring-2"
              disabled={submitting}
            />
          </div>
        </div>

        <div>
          <label htmlFor="password" className="text-ink-300 mb-1.5 block text-xs font-semibold">
            Senha
          </label>
          <div className="relative">
            <Lock size={14} className="text-ink-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="border-ink-800 text-ink-100 placeholder:text-ink-500 focus:border-navy-primary focus:ring-navy-primary/20 w-full rounded-lg border bg-white py-2.5 pl-9 pr-10 text-sm outline-none focus:ring-2"
              disabled={submitting}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="text-ink-500 hover:text-ink-300 absolute right-3 top-1/2 -translate-y-1/2"
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="bg-navy-primary hover:bg-navy-primary/90 shadow-soft mt-2 w-full rounded-lg py-3 text-sm font-semibold text-white transition-colors disabled:opacity-50"
        >
          {submitting ? 'Entrando…' : 'Entrar'}
        </button>

        <div className="flex items-center justify-between pt-2">
          <Link
            to="/forgot-password"
            className="text-navy-primary hover:text-navy-primary/80 text-xs font-medium"
          >
            Esqueci minha senha
          </Link>
          <span className="text-2xs text-ink-500">Suporte: suporte@pnlglobal.com.br</span>
        </div>
      </form>
    </AuthLayout>
  );
}
